/**
 * Web push for shoppers: "tell me when someone replies".
 *
 * A service worker must be served from the page's own origin, and the
 * widget runs on someone else's — so push is opt-in per site. The client
 * hosts a one-line file (`importScripts("https://cdn…/v1/sw.js")`) and names
 * it in the embed (`data-service-worker="/cos-sw.js"`). Without that, none
 * of this runs and the shopper is never asked.
 *
 * The browser's own permission dialog is shown only after the shopper says
 * yes to ours. A "Block" in the browser's dialog is effectively permanent,
 * so it is never spent on a shopper who hasn't already said they want this.
 */

const DISMISS_KEY = "cos_push_dismissed";
/** A "No thanks" holds for a month; asking every visit is how a widget gets muted. */
const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;

export interface PushDeps {
  apiUrl: string;
  /** The path of the client-hosted service worker, from the embed. */
  serviceWorker?: string;
  token: () => string | undefined;
}

function supported(deps: PushDeps): boolean {
  return Boolean(deps.serviceWorker) && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function dismissedRecently(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(DISMISS_KEY) ?? 0) < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

export function rememberDismissal(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // The prompt simply comes back next visit.
  }
}

let keyPromise: Promise<string | null> | undefined;
function publicKey(apiUrl: string): Promise<string | null> {
  keyPromise ??= fetch(new URL("/api/push/config", apiUrl))
    .then((response) => (response.ok ? response.json() : { publicKey: null }))
    .then((body: { publicKey: string | null }) => body.publicKey)
    .catch(() => null);
  return keyPromise;
}

/** Whether to show our soft prompt now: supported, not yet decided in the browser, not recently declined, and push is on server-side. */
export async function canOffer(deps: PushDeps): Promise<boolean> {
  if (!supported(deps) || Notification.permission !== "default" || dismissedRecently()) return false;
  return (await publicKey(deps.apiUrl)) !== null;
}

function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const raw = atob(`${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Subscribes this browser and tells the API which visitor it belongs to.
 * `ask` is false for the silent path: permission was already granted on an
 * earlier visit, so a new session just re-registers (keys rotate) without
 * prompting. Returns whether the browser ended up subscribed.
 */
export async function subscribe(deps: PushDeps, ask: boolean): Promise<boolean> {
  if (!supported(deps)) return false;
  const key = await publicKey(deps.apiUrl);
  const token = deps.token();
  if (!key || !token) return false;
  if (ask && (await Notification.requestPermission()) !== "granted") return false;
  if (Notification.permission !== "granted") return false;

  try {
    const registration = await navigator.serviceWorker.register(deps.serviceWorker!);
    await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
    const url = new URL("/api/widget/push-subscription", deps.apiUrl);
    url.searchParams.set("token", token);
    const response = await fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(subscription) });
    return response.ok;
  } catch {
    // Most often: the site never actually hosted the worker file (a 404 on
    // registration). The shopper loses a notification, not the chat.
    return false;
  }
}

/**
 * A notification click lands the shopper back on the page with the chat
 * open: either the worker messages a tab that's already open, or it opens
 * one at `…#cos-chat`, which is consumed (and removed) here on load.
 */
export function listenForOpen(open: () => void): void {
  if (location.hash === "#cos-chat") {
    history.replaceState(history.state, "", location.href.replace(/#cos-chat$/, ""));
    open();
  }
  navigator.serviceWorker?.addEventListener("message", (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === "cos:open-chat") open();
  });
}
